// src/features/leases/leasePortfolioMetrics.js
//
// LSE-LANDING-PAGE — Pure derived-metric helpers for the Leases landing page.
//
// All functions are stateless (no React, no hooks). The landing page calls
// them inside useMemo so they re-run only when their inputs change.
//
// Lease shape expected by most helpers (from GET /api/leases/portfolio):
//   id, locationId, leaseType, status, startDate, endDate,
//   soonestPossibleExpiration, nextNoticeDeadline,
//   totalCost, currency, size, areaType, city, region
//
// L52 cite: termRemainingBucket is the SSOT for landing-page bucketing.
// Every "by year" and "by region" breakdown uses this function.
// Bar colors route through T tokens (no inline hex) and share URGENT_RED /
// T.amber with LeaseTimeline so the two surfaces stay visually consistent.

import { resolveTimelineEndAnchor, parseDateOnly, isSentinelEndDate, URGENT_RED, ACTIVE_LABEL, UPCOMING_LABEL, URGENT_LABEL, EXPIRED_LABEL } from "./leaseTabConfig.js";
import { convertAmount } from "../../utils/fxConvert.js";
import { T } from "../../app/tokens.js";
import { annualRentArea } from "../../shared/leaseAnnualHeadlines.js";

// ─── Term-remaining buckets ────────────────────────────────────────────────
//
// LSE-LEASES-LIST-URGENCY-COLUMN-FOLLOWUP: 4-bucket scheme aligned with the
// List view URGENCY column (leaseUrgencyBucket in leaseTabConfig):
//   ACTIVE_3YR_PLUS → "Active (>2yr)"   T.leaseGanttGreen — key name kept for
//                      compatibility; threshold reduced from 3yr to 2yr.
//   UPCOMING_1_TO_3 → "Upcoming (1–2yr)" T.amber           — same key, new label.
//   URGENT_UNDER_1  → "Urgent (<1yr)"    URGENT_RED
//   EXPIRED         → "Expired"          T.defaultDot (LSE-LEASES-URGENCY-EXPIRED-GRAY-MATCH-LOCATIONS)

// LSE-LEASES-URGENCY-COLOR-MATCH-LOCATIONS: ACTIVE and URGENT colors now use
// T.green / T.red — same tokens as Locations HEALTH — for visual parity.
export const TERM_REMAINING_BUCKETS = {
  // minDays is the inclusive lower bound (in days) for each bucket.
  // DaysRemainingPill reads these to stay in sync with bar colors.
  ACTIVE_3YR_PLUS:  { label: `${ACTIVE_LABEL} (>2yr)`,    color: T.green,      minDays: 365 * 2 },
  UPCOMING_1_TO_3:  { label: `${UPCOMING_LABEL} (1-2yr)`, color: T.amber,      minDays: 365     },
  URGENT_UNDER_1:   { label: `${URGENT_LABEL} (<1yr)`,    color: T.red,        minDays: 0       },
  // Rolling: sentinel end_date (9999-12-31), no fixed contractual cliff.
  // color=T.amber is the fallback for non-Gantt contexts; the Gantt bar uses
  // the diagonal stripe pattern (fill="url(#rolling-stripes)") when stripe===true.
  ROLLING:          { label: "Rolling",                    color: T.amber, stripe: true },
  EXPIRED:          { label: EXPIRED_LABEL,                color: T.defaultDot },
};

// LSE-LANDING-PAGE-FOLLOWUP-1 (Bug 6): mode-aware urgency demotion.
// When anchor.mode is projected_expiration or projected_notice, the lease is
// in a rolling notice window — that is steady-state, not an alarm. Demote
// to UPCOMING_1_TO_3 even if days < 365.
//
// LSE-LEASES-LIST-URGENCY-COLUMN-FOLLOWUP: sentinel/ongoing leases (no fixed
// end date) fall back to ACTIVE_3YR_PLUS — not time-pressured. Threshold
// updated 3yr→2yr.
export function termRemainingBucket(lease, now = new Date()) {
  if (lease.status === "expired") return TERM_REMAINING_BUCKETS.EXPIRED;
  // Sentinel end_date → ROLLING bucket (diagonal stripe on Gantt, half-circle
  // on List URGENCY). No contractual cliff; separate from time-based urgency.
  // (LSE-GANTT-ROLLING-STRIPES-AND-TOOLTIP-CLEANUP supersedes prior "solid green"
  //  guidance from LSE-ROLLING-VISUAL-AND-DETECTION-CORRECTION)
  if (isSentinelEndDate(lease.endDate)) return TERM_REMAINING_BUCKETS.ROLLING;
  const anchor = resolveTimelineEndAnchor(lease);
  if (anchor.mode === "ongoing" || !anchor.date) return TERM_REMAINING_BUCKETS.ACTIVE_3YR_PLUS;
  const endDate = parseDateOnly(anchor.date);
  if (!endDate) return TERM_REMAINING_BUCKETS.ACTIVE_3YR_PLUS;
  const days = (endDate - now) / (1000 * 60 * 60 * 24);

  // Projected (rolling notice) leases are never URGENT — they live in that
  // window by design. Only contractual / undefined modes can be urgent.
  const isProjected = anchor.mode === "projected_expiration"
                   || anchor.mode === "projected_notice";

  if (days < 365 && !isProjected) return TERM_REMAINING_BUCKETS.URGENT_UNDER_1;
  if (days < 365 * 2)             return TERM_REMAINING_BUCKETS.UPCOMING_1_TO_3;
  return TERM_REMAINING_BUCKETS.ACTIVE_3YR_PLUS;
}

// ─── KPI aggregations ──────────────────────────────────────────────────────

// LSE-LANDING-PAGE-FOLLOWUP-1 (Bug 5): returns { total, count, excludedCount }
// where excludedCount = leases with a non-null totalCost that were skipped
// because no FX rate was available to convert them to USD.
// Callers render a subtitle like "N excluded (no FX rate)" when excludedCount > 0.
//
// No internal status filter — callers pass the already-filtered `filteredLeases`
// set so cost metrics follow the selected status filter (expired, all, etc.).
export function computeAnnualPortfolioCost(leases, fxRates) {
  let total         = 0;
  let count         = 0;
  let excludedCount = 0;
  for (const l of leases) {
    count++;
    if (l.totalCost == null) continue;
    const usd = convertAmount(Number(l.totalCost), l.currency, "USD", fxRates);
    if (usd != null) {
      total += usd;
    } else {
      excludedCount++;
    }
  }
  return { total, count, excludedCount };
}

// Cost-weighted average of days remaining across the input lease set.
// Uses resolveTimelineEndAnchor per lease so projected/ongoing leases
// bucket sensibly. Returns days as a number (or 0 when no leases qualify).
// Leases whose end anchor is in the past (days < 0) are naturally excluded.
// No internal status filter — callers pass the already-filtered filteredLeases.
export function computeAvgLeaseTermRemaining(leases, fxRates, now = new Date()) {
  let weightedSum = 0;
  let totalWeight = 0;
  for (const l of leases) {
    const anchor = resolveTimelineEndAnchor(l);
    if (!anchor.date) continue;
    const endDate = parseDateOnly(anchor.date);
    if (!endDate) continue;
    const days = (endDate - now) / (1000 * 60 * 60 * 24);
    if (days < 0) continue;
    const cost = l.totalCost != null
      ? (convertAmount(Number(l.totalCost), l.currency, "USD", fxRates) ?? 1)
      : 1;
    weightedSum += days * cost;
    totalWeight += cost;
  }
  if (totalWeight === 0) return 0;
  return weightedSum / totalWeight;
}

// LSE-LANDING-PAGE-FOLLOWUP-1 (Bug 1):
//
// Returns null when no SqFt leases with non-null, non-zero size are present.
// Otherwise returns { value, sqftCount, activeCount } where:
//   value        — USD cost per sqft
//   sqftCount    — number of leases that contributed sqft data
//   activeCount  — total leases in the input subset (named for backward-compat;
//                  callers pass the already-filtered filteredLeases so this
//                  reflects the selected status, not just "active" leases)
//
// No internal status filter — callers pass the already-filtered filteredLeases
// so cost metrics follow the selected status filter (expired, all, etc.).
//
// This lets the caller display "N of M leases with sqft data" so the user
// knows when the denominator is partial. sqftCount === 0 case returns null
// (guarded above) so the display always falls back to "—" cleanly.
export function computeLeaseCostPerSqft(leases, fxRates) {
  let totalUsd  = 0;
  let totalSqft = 0;
  let sqftCount  = 0;
  let activeCount = 0;
  for (const l of leases) {
    activeCount++;
    const area = annualRentArea(l);
    if (l.annualRentUsd == null || area == null) continue;
    const sz = area;
    const usd = Number(l.annualRentUsd);
    if (!Number.isFinite(sz) || sz <= 0) continue; // Bug 1: guard zero/NaN
    if (!Number.isFinite(usd)) continue;
    totalUsd  += usd;
    totalSqft += sz;
    sqftCount++;
  }
  if (totalSqft === 0) return null;
  return { value: totalUsd / totalSqft, sqftCount, activeCount };
}

// Undrawn concessions KPI.
// unspentByCurrency: [{currency, unspent}] from getUnspentAllowances
// criticalDates:     CriticalDateRow[] from getCriticalDates (optional)
export function computeUndrawnConcessions(unspentByCurrency, fxRates, criticalDates = []) {
  let total = 0;
  for (const row of (unspentByCurrency || [])) {
    const usd = convertAmount(Number(row.unspent), row.currency, "USD", fxRates);
    if (usd != null) total += usd;
  }

  const expiries = (criticalDates || [])
    .filter((cd) => cd.type === "allowance_expiry" && cd.dueDate)
    .map((cd) => cd.dueDate)
    .sort();

  const nextExpiry = expiries.length > 0 ? expiries[expiries.length - 1] : null;

  return { amount: total, nextExpiry };
}

// ─── Analytics panels ─────────────────────────────────────────────────────

export function computeAnnualCostByRegion(leases, fxRates) {
  const byRegion = {};
  let grandTotal = 0;
  for (const l of leases) {
    if (l.status !== "active" || l.totalCost == null) continue;
    const usd = convertAmount(Number(l.totalCost), l.currency, "USD", fxRates);
    if (usd == null) continue;
    const region = l.region || "Other";
    byRegion[region] = (byRegion[region] || 0) + usd;
    grandTotal += usd;
  }
  const regions = Object.entries(byRegion)
    .sort((a, b) => b[1] - a[1])
    .map(([region, amount]) => ({
      region,
      amount,
      pct: grandTotal > 0 ? (amount / grandTotal) * 100 : 0,
    }));
  return { regions, grandTotal };
}

// Returns [{year, count, bucket}] where bucket is the termRemainingBucket
// of that year relative to now (i.e., how urgent is it that leases expire
// in that year).
//
// Gap A11 (LSE-LANDING-PAGE-FOLLOWUP-2): counts ONLY leases with a real
// contractual end_date. Sentinel values (9999-12-31) represent rolling /
// ongoing leases that will NOT expire in a calendar year — bucketing them
// via resolveTimelineEndAnchor was clustering serviced-office decision
// windows into 2026 (8 sentinel vs 4 real confirmed by DB query). Using
// lease.endDate directly and skipping sentinels gives the contractual chart.
// Confirmed: 8 of 12 "2026" leases were sentinel noise; 4 are real.
export function computeExpirationsByYear(leases, now = new Date()) {
  const yearMap = {};
  for (const l of leases) {
    // Skip sentinel end_dates — these are rolling/ongoing leases with no
    // contractual expiration year. The sentinel value is "9999-12-31".
    const endDateStr = l.endDate;
    if (!endDateStr || endDateStr.startsWith("9999")) continue;
    const endDate = parseDateOnly(endDateStr);
    if (!endDate) continue;
    const year = endDate.getFullYear();
    if (!yearMap[year]) yearMap[year] = { count: 0, bucket: null };
    yearMap[year].count++;
    const fakeLease = { status: l.status, endDate: `${year}-12-31` };
    yearMap[year].bucket = termRemainingBucket(fakeLease, now);
  }
  return Object.entries(yearMap)
    .map(([year, { count, bucket }]) => ({ year: Number(year), count, bucket }))
    .sort((a, b) => a.year - b.year);
}

// ─── Formatting helpers ───────────────────────────────────────────────────

export function fmtMillions(amount) {
  if (amount == null || !Number.isFinite(amount)) return "—";
  if (Math.abs(amount) >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(1)}M USD`;
  }
  if (Math.abs(amount) >= 1_000) {
    return `${(amount / 1_000).toFixed(0)}K USD`;
  }
  return `${Math.round(amount).toLocaleString()} USD`;
}

export function fmtYears(days) {
  if (!days || days <= 0) return "—";
  const yrs = days / 365;
  return `${yrs.toFixed(1)}yr`;
}

export function fmtCostPerSqft(val) {
  if (val == null || !Number.isFinite(val)) return "—";
  return `${Math.round(val)} USD`;
}

export function fmtDaysRemaining(lease, now = new Date()) {
  if (lease.status === "expired") return null;
  const anchor = resolveTimelineEndAnchor(lease);
  if (!anchor.date) return null;
  const endDate = parseDateOnly(anchor.date);
  if (!endDate) return null;
  const days = Math.round((endDate - now) / (1000 * 60 * 60 * 24));
  return days;
}
